import { useErpProvisioning } from '@affine/core/modules/erp';
import {
  Button,
  ErrorMessage,
  Input,
  notify,
  Skeleton,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { AIModelService } from '@affine/core/modules/ai-button/services/models';
import { LlmProviderService } from '@affine/core/modules/cloud';
import type {
  LlmModel,
  LlmProvider,
} from '@affine/core/modules/cloud/stores/llm-provider';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { AiIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import {
  IntegrationSettingHeader,
  IntegrationSettingTextRadioGroup,
  IntegrationSettingToggle,
} from '../setting';
import * as styles from './setting-panel.css';

const PROVIDER_KIND_ITEMS = [
  { name: 'Self-hosted', value: 'self_hosted' },
  { name: 'API', value: 'api' },
];

const kindLabel = (kind: string) =>
  kind === 'self_hosted' ? 'Self-hosted' : kind === 'api' ? 'API' : kind;

const AiModelsSettingHeader = () => {
  const t = useI18n();

  return (
    <IntegrationSettingHeader
      icon={<AiIcon />}
      name={t['com.affine.integration.ai-models.name']()}
      desc={t['com.affine.integration.ai-models.desc']()}
    />
  );
};

export const AiModelsSettingPanel = () => {
  const allowed = useErpProvisioning()?.canUseAiAgents ?? false;
  return allowed ? <AllowedAiModelsSettingPanel /> : null;
};

const AllowedAiModelsSettingPanel = () => {
  return <AiModelsSetting />;
};

const AiModelsSetting = () => {
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const svc = useService(LlmProviderService);
  const aiModelService = useService(AIModelService);
  const permissionService = useService(WorkspacePermissionService);

  const providers = useLiveData(svc.providers$);
  const isRevalidating = useLiveData(svc.isRevalidating$);
  const error = useLiveData(svc.error$);
  const isOwnerOrAdmin = useLiveData(
    permissionService.permission.isOwnerOrAdmin$
  );

  // `null` means "not yet resolved" — treat undecided as allowed so we never
  // flash a false "no access" notice. Only an explicit `false` disables the UI.
  const canManage = isOwnerOrAdmin !== false;

  useEffect(() => {
    svc.revalidate(workspaceId);
  }, [svc, workspaceId]);

  useEffect(() => {
    permissionService.permission.revalidate();
  }, [permissionService]);

  const refreshDropdown = useCallback(async () => {
    await aiModelService.refresh(workspaceId);
  }, [aiModelService, workspaceId]);

  const showLoading = providers === null && isRevalidating;
  const showError = providers === null && error !== null;

  if (showLoading) {
    return (
      <div>
        <AiModelsSettingHeader />
        <Skeleton />
      </div>
    );
  }

  if (showError) {
    return (
      <div>
        <AiModelsSettingHeader />
        <ErrorMessage>{String(error)}</ErrorMessage>
      </div>
    );
  }

  return (
    <div>
      <AiModelsSettingHeader />

      {!canManage ? (
        <div className={styles.noticeBanner}>
          You need admin access to manage AI models in this workspace.
        </div>
      ) : null}

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Providers</div>
        </div>
        <p className={styles.sectionDescription}>
          LLM providers and their models available to AI in this workspace.
        </p>

        {providers && providers.length > 0 ? (
          providers.map(provider => (
            <ProviderRow
              key={provider.id}
              provider={provider}
              workspaceId={workspaceId}
              svc={svc}
              refreshDropdown={refreshDropdown}
              disabled={!canManage}
            />
          ))
        ) : (
          <div className={styles.emptyHint}>No providers configured yet.</div>
        )}
      </div>

      <AddProviderForm
        workspaceId={workspaceId}
        svc={svc}
        refreshDropdown={refreshDropdown}
        disabled={!canManage}
      />
    </div>
  );
};

interface ProviderRowProps {
  provider: LlmProvider;
  workspaceId: string;
  svc: LlmProviderService;
  refreshDropdown: () => Promise<void>;
  disabled: boolean;
}

const ProviderRow = ({
  provider,
  workspaceId,
  svc,
  refreshDropdown,
  disabled,
}: ProviderRowProps) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(provider.name);
  const [kind, setKind] = useState(provider.kind);
  const [baseUrl, setBaseUrl] = useState(provider.baseUrl);
  const [apiKey, setApiKey] = useState('');
  const [mutating, setMutating] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const startEditing = useCallback(() => {
    setName(provider.name);
    setKind(provider.kind);
    setBaseUrl(provider.baseUrl);
    setApiKey('');
    setEditing(true);
  }, [provider]);

  const handleSave = useAsyncCallback(async () => {
    setMutating(true);
    try {
      // Blank apiKey is omitted so the backend keeps the existing key.
      await svc.upsertProvider(workspaceId, {
        id: provider.id,
        name,
        kind,
        baseUrl,
        apiKey: apiKey || undefined,
      });
      await refreshDropdown();
      setEditing(false);
      setApiKey('');
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setMutating(false);
    }
  }, [
    svc,
    workspaceId,
    provider.id,
    name,
    kind,
    baseUrl,
    apiKey,
    refreshDropdown,
  ]);

  const handleDelete = useAsyncCallback(async () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setMutating(true);
    try {
      await svc.deleteProvider(workspaceId, provider.id);
      await refreshDropdown();
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setMutating(false);
      setConfirmingDelete(false);
    }
  }, [svc, workspaceId, provider.id, confirmingDelete, refreshDropdown]);

  return (
    <div className={styles.subSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>{provider.name}</div>
        <div className={styles.rowActions}>
          {!editing ? (
            <Button onClick={startEditing} disabled={disabled || mutating}>
              Edit
            </Button>
          ) : (
            <Button
              onClick={() => {
                setEditing(false);
                setApiKey('');
              }}
              disabled={mutating}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="error"
            onClick={handleDelete}
            disabled={disabled || mutating}
          >
            {confirmingDelete ? 'Confirm delete' : 'Delete'}
          </Button>
        </div>
      </div>

      <div className={styles.inlineMeta}>
        <span className={styles.kindBadge}>{kindLabel(provider.kind)}</span>
        <span>{provider.baseUrl}</span>
        <span>
          {provider.keyMasked ? `Key: ${provider.keyMasked}` : 'No key'}
        </span>
        <span>{provider.enabled ? 'Enabled' : 'Disabled'}</span>
      </div>

      {editing ? (
        <>
          <div className={styles.formRow}>
            <span className={styles.formLabel}>Name</span>
            <Input value={name} onChange={setName} disabled={mutating} />
          </div>
          <div className={styles.formRow}>
            <span className={styles.formLabel}>Kind</span>
            <IntegrationSettingTextRadioGroup
              items={PROVIDER_KIND_ITEMS}
              checked={kind}
              onChange={setKind}
            />
          </div>
          <div className={styles.formRow}>
            <span className={styles.formLabel}>Base URL</span>
            <Input value={baseUrl} onChange={setBaseUrl} disabled={mutating} />
          </div>
          <div className={styles.formRow}>
            <span className={styles.formLabel}>API key</span>
            <Input
              type="password"
              value={apiKey}
              onChange={setApiKey}
              placeholder="leave blank to keep existing key"
              disabled={mutating}
            />
          </div>
          <div className={styles.formActions}>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={mutating || !name || !baseUrl}
            >
              Save
            </Button>
          </div>
        </>
      ) : null}

      <ModelsList
        provider={provider}
        workspaceId={workspaceId}
        svc={svc}
        refreshDropdown={refreshDropdown}
        disabled={disabled}
      />
    </div>
  );
};

interface ModelsListProps {
  provider: LlmProvider;
  workspaceId: string;
  svc: LlmProviderService;
  refreshDropdown: () => Promise<void>;
  disabled: boolean;
}

const ModelsList = ({
  provider,
  workspaceId,
  svc,
  refreshDropdown,
  disabled,
}: ModelsListProps) => {
  return (
    <div className={styles.formRow}>
      <span className={styles.formLabel}>Models</span>
      {provider.models.length > 0 ? (
        provider.models.map(model => (
          <ModelRow
            key={model.id}
            model={model}
            workspaceId={workspaceId}
            svc={svc}
            refreshDropdown={refreshDropdown}
            disabled={disabled}
          />
        ))
      ) : (
        <div className={styles.emptyHint}>No models yet.</div>
      )}
      <AddModelForm
        providerId={provider.id}
        workspaceId={workspaceId}
        svc={svc}
        refreshDropdown={refreshDropdown}
        disabled={disabled}
      />
    </div>
  );
};

interface ModelRowProps {
  model: LlmModel;
  workspaceId: string;
  svc: LlmProviderService;
  refreshDropdown: () => Promise<void>;
  disabled: boolean;
}

const ModelRow = ({
  model,
  workspaceId,
  svc,
  refreshDropdown,
  disabled,
}: ModelRowProps) => {
  const [mutating, setMutating] = useState(false);

  const handleToggleEnabled = useAsyncCallback(
    async (checked: boolean) => {
      setMutating(true);
      try {
        await svc.setModelEnabled(workspaceId, model.id, checked);
        await refreshDropdown();
      } catch (err) {
        notify.error({ error: UserFriendlyError.fromAny(err) });
      } finally {
        setMutating(false);
      }
    },
    [svc, workspaceId, model.id, refreshDropdown]
  );

  const handleSetDefault = useAsyncCallback(async () => {
    setMutating(true);
    try {
      await svc.setDefaultModel(workspaceId, model.id);
      await refreshDropdown();
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setMutating(false);
    }
  }, [svc, workspaceId, model.id, refreshDropdown]);

  const handleDelete = useAsyncCallback(async () => {
    setMutating(true);
    try {
      await svc.deleteModel(workspaceId, model.id);
      await refreshDropdown();
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setMutating(false);
    }
  }, [svc, workspaceId, model.id, refreshDropdown]);

  return (
    <div className={styles.subSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          {model.displayName}
          {model.isDefault ? (
            <span className={styles.defaultBadge} style={{ marginLeft: 8 }}>
              Default
            </span>
          ) : null}
        </div>
        <div className={styles.rowActions}>
          {!model.isDefault ? (
            <Button
              onClick={handleSetDefault}
              disabled={disabled || mutating || !model.enabled}
            >
              Set default
            </Button>
          ) : null}
          <Button
            variant="error"
            onClick={handleDelete}
            disabled={disabled || mutating}
          >
            Delete
          </Button>
        </div>
      </div>
      <div className={styles.inlineMeta}>
        <span>{model.modelId}</span>
      </div>
      <IntegrationSettingToggle
        name="Enabled"
        checked={model.enabled}
        onChange={disabled || mutating ? () => {} : handleToggleEnabled}
      />
    </div>
  );
};

interface AddProviderFormProps {
  workspaceId: string;
  svc: LlmProviderService;
  refreshDropdown: () => Promise<void>;
  disabled: boolean;
}

const AddProviderForm = ({
  workspaceId,
  svc,
  refreshDropdown,
  disabled,
}: AddProviderFormProps) => {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('self_hosted');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [mutating, setMutating] = useState(false);

  const reset = useCallback(() => {
    setName('');
    setKind('self_hosted');
    setBaseUrl('');
    setApiKey('');
  }, []);

  const handleAdd = useAsyncCallback(async () => {
    setMutating(true);
    try {
      await svc.upsertProvider(workspaceId, {
        name,
        kind,
        baseUrl,
        apiKey: apiKey || undefined,
      });
      await refreshDropdown();
      reset();
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setMutating(false);
    }
  }, [svc, workspaceId, name, kind, baseUrl, apiKey, refreshDropdown, reset]);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>Add provider</div>
      </div>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>Name</span>
        <Input
          value={name}
          onChange={setName}
          placeholder="e.g. Local Ollama"
          disabled={disabled || mutating}
        />
      </div>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>Kind</span>
        <IntegrationSettingTextRadioGroup
          items={PROVIDER_KIND_ITEMS}
          checked={kind}
          onChange={setKind}
        />
      </div>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>Base URL</span>
        <Input
          value={baseUrl}
          onChange={setBaseUrl}
          placeholder="https://..."
          disabled={disabled || mutating}
        />
      </div>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>API key (optional)</span>
        <Input
          type="password"
          value={apiKey}
          onChange={setApiKey}
          placeholder="leave blank for none"
          disabled={disabled || mutating}
        />
      </div>
      <div className={styles.formActions}>
        <Button
          variant="primary"
          onClick={handleAdd}
          disabled={disabled || mutating || !name || !baseUrl}
        >
          Add provider
        </Button>
      </div>
    </div>
  );
};

interface AddModelFormProps {
  providerId: string;
  workspaceId: string;
  svc: LlmProviderService;
  refreshDropdown: () => Promise<void>;
  disabled: boolean;
}

const AddModelForm = ({
  providerId,
  workspaceId,
  svc,
  refreshDropdown,
  disabled,
}: AddModelFormProps) => {
  const [modelId, setModelId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [mutating, setMutating] = useState(false);

  const handleAdd = useAsyncCallback(async () => {
    setMutating(true);
    try {
      await svc.upsertModel(workspaceId, {
        providerId,
        modelId,
        displayName,
      });
      await refreshDropdown();
      setModelId('');
      setDisplayName('');
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setMutating(false);
    }
  }, [svc, workspaceId, providerId, modelId, displayName, refreshDropdown]);

  return (
    <div className={styles.subSection}>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>Add model</span>
        <Input
          value={modelId}
          onChange={setModelId}
          placeholder="model id (e.g. llama3.1:8b)"
          disabled={disabled || mutating}
        />
        <Input
          value={displayName}
          onChange={setDisplayName}
          placeholder="display name"
          disabled={disabled || mutating}
        />
      </div>
      <div className={styles.formActions}>
        <Button
          variant="primary"
          onClick={handleAdd}
          disabled={disabled || mutating || !modelId || !displayName}
        >
          Add model
        </Button>
      </div>
    </div>
  );
};
